import axios from 'axios';
import {
  fetchLiveCached,
  invalidateLiveEntityCache,
  LIVE_ENTITIES,
  METRICS_TTL_MS,
  metricsTtlFor,
  resolveMutationInvalidation,
  searchTermsCacheScope,
} from '../utils/liveEntityCache';
import { normalizeCampaignType } from '../utils/amazonCampaignType';

// Use relative API path by default so dev proxy handles port differences
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export const AMAZON_RULE_CREATE_TIMEOUT_MS = 300000;

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased to 60 seconds
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/** Create Amazon rule and link campaigns (parallel on server; allow several minutes for large scopes). */
export function createAmazonRule(payload) {
  return api.post('/amazon/rules', payload, { timeout: AMAZON_RULE_CREATE_TIMEOUT_MS });
}

const ACCESS_TOKEN_KEY = 'dg_access_token';

function readStoredAccessToken() {
  try {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeStoredAccessToken(token) {
  try {
    if (token) {
      sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    } else {
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    }
  } catch {
    // Ignore private-mode / blocked storage
  }
}

// Access token lives in memory and sessionStorage so a page refresh stays logged in.
let accessToken = readStoredAccessToken();
let refreshPromise = null;

if (accessToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
}

export function setAccessToken(token) {
  accessToken = token || null;
  if (accessToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
  writeStoredAccessToken(accessToken);
}

export function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = api
      .post('/auth/refresh')
      .then((res) => {
        if (res.data?.success && res.data?.token) {
          setAccessToken(res.data.token);
          return res.data;
        }
        throw new Error(res.data?.message || 'Refresh failed');
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — auth errors + invalidate live entity cache after mutations
api.interceptors.response.use(
  (response) => {
    try {
      const invalidation = resolveMutationInvalidation(response.config);
      if (invalidation && response.status < 400) {
        invalidateLiveEntityCache(invalidation.profileId, invalidation.entities);
      }
    } catch {
      // Cache invalidation must never break API responses
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    const isAuthEndpoint = originalRequest?.url?.startsWith('/auth/');
    const isRefreshCall = originalRequest?.url?.startsWith('/auth/refresh');
    const isOnLoginRoute =
      window.location.pathname === '/login' || window.location.pathname === '/login/qc';

    if (error.response?.status === 401 && !isRefreshCall) {
      if (!isAuthEndpoint && !originalRequest?._retry) {
        try {
          const data = await refreshSession();
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${data.token}`;
          return api(originalRequest);
        } catch (refreshError) {
          setAccessToken(null);
          if (!isOnLoginRoute) {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// API endpoints
export const endpoints = {
  // Auth
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    profile: '/auth/profile',
    logout: '/auth/logout',
    changePassword: '/auth/change-password',
  },
  
  // Dashboard & Analytics
  analytics: {
    dashboard: '/analytics/dashboard',
    trends: '/analytics/trends',
    campaignsComparison: '/analytics/campaigns/comparison',
    productsComparison: '/analytics/products/comparison',
    keywords: '/analytics/keywords',
    searchTerms: '/analytics/search-terms',
    geographic: '/analytics/geographic',
    devices: '/analytics/devices',
  },
  
  // Campaigns
  campaigns: {
    list: '/campaigns',
    detail: (id) => `/campaigns/${id}`,
    metrics: (id) => `/campaigns/${id}/metrics`,
    create: '/campaigns',
    update: (id) => `/campaigns/${id}`,
    delete: (id) => `/campaigns/${id}`,
    bulkUpdate: '/campaigns/bulk',
  },
  
  // Products
  products: {
    list: '/products',
    detail: (id) => `/products/${id}`,
    byAsin: (asin) => `/products/asin/${asin}`,
    metrics: (id) => `/products/${id}/metrics`,
    campaigns: (id) => `/products/${id}/campaigns`,
    update: (id) => `/products/${id}`,
    bulkUpdate: '/products/bulk',
    categories: '/products/meta/categories',
    brands: '/products/meta/brands',
  },
  
  // Amazon API
  amazon: {
    // OAuth
    authUrl: '/amazon/auth/url',
    authCallback: '/amazon/auth/callback',
    
    // Core endpoints
    profiles: '/amazon/profiles',
    dashboard: '/amazon/dashboard',
    
    // Campaigns
    campaigns: '/amazon/campaigns',
    campaignsMetrics: '/amazon/campaigns/metrics',
    createCampaign: '/amazon/campaigns',
    
    // Ad Groups
    adGroups: '/amazon/adGroups',
    adGroupsPerformance: '/performance/adgroups',
    keywordsPerformance: '/performance/keywords',
    
    // Keywords
    keywords: '/amazon/keywords',
    negativeKeywords: '/amazon/negativeKeywords',
    campaignNegativeKeywords: '/amazon/campaignNegativeKeywords',
    
    // Product Ads
    productAds: '/amazon/productAds',
    
    // Reports (old v2 endpoint - deprecated)
    reports: '/amazon/reports/campaigns',
    
    // Comprehensive Reports API (Production-ready)
    reportCampaigns: '/amazon/reports/campaigns',
    reportAdGroups: '/amazon/reports/adgroups',
    reportKeywords: '/amazon/reports/keywords',
    reportSearchTerms: '/amazon/reports/searchterms',

    // Master Business Report — all levels, all ad types, one call
    masterReport: '/master-report',
    bulkSnapshot: '/amazon/bulk-snapshot',
    bulkSnapshotBundle: '/amazon/bulk-snapshot-bundle',
    
    // Reporting API (Working endpoints for cost/spend data)
    reportingCreate: '/amazon/reporting/reports',
    reportingStatus: (reportId) => `/amazon/reporting/reports/${reportId}`,
    reportingDownload: (reportId) => `/amazon/reporting/reports/${reportId}/download`,
    reportingCached: '/amazon/reports/cached', // Fast cached endpoint
    reportingMetadata: '/amazon/reports/metadata', // Cache metadata (last refresh, status)
    
    // Targeting
    targetingSuggestions: '/amazon/targeting/suggestions',
    
    // Auto Targeting (for auto campaigns)
    targets: '/amazon/targets',
    targetBidRecommendations: '/amazon/targets/bid-recommendations',
    
    // Budgets
    budgets: '/amazon/budgets',
  },
};

// Feature flag to use mock data for Amazon endpoints
const USE_MOCK_AMAZON = process.env.REACT_APP_USE_MOCK_AMAZON === 'true';

// Simple mock helpers
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const mockResponse = async (data, ms = 400) => {
  await delay(ms);
  return { data };
};

// Utility functions for common API calls
export const apiUtils = {
  // Generic GET request
  get: (url, params = {}) => api.get(url, { params }),
  
  // Generic POST request
  post: (url, data = {}) => api.post(url, data),
  
  // Generic PUT request
  put: (url, data = {}) => api.put(url, data),
  
  // Generic PATCH request
  patch: (url, data = {}) => api.patch(url, data),
  
  // Generic DELETE request
  delete: (url) => api.delete(url),
  
  // File upload
  upload: (url, formData, onUploadProgress) => 
    api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    }),
};

// Amazon API specific utility functions
export const amazonApi = {
  // Get OAuth URL
  getAuthUrl: () => api.get(endpoints.amazon.authUrl),
  
  // Handle OAuth callback
  handleAuthCallback: (code) => api.post(endpoints.amazon.authCallback, { code }),
  
  // Get all profiles
  getProfiles: () => {
    if (USE_MOCK_AMAZON) {
      return mockResponse({
        success: true,
        data: [
          { profileId: 'P-001', name: 'Acme US', countryCode: 'US', marketplace: 'US', currencyCode: 'USD' },
          { profileId: 'P-002', name: 'Acme UK', countryCode: 'UK', marketplace: 'UK', currencyCode: 'GBP' },
          { profileId: 'P-003', name: 'Acme DE', countryCode: 'DE', marketplace: 'DE', currencyCode: 'EUR' },
        ],
      });
    }
    return api.get(endpoints.amazon.profiles);
  },
  
  // Get dashboard data for a profile
  getDashboardData: (profileId, startDate, endDate) => {
    // Always return mock structure for now (will be overridden by real ad spend data)
    if (true) {
      // Calculate date-based multipliers for realistic changes
      const getDaysMultiplier = (range) => {
        const multipliers = {
          'today': 1/7,
          '7d': 1,
          '30d': 30/7,
          '90d': 90/7
        };
        return multipliers[range] || 1;
      };

      const campaigns = Array.from({ length: 10 }, (_, i) => ({
        campaignId: `C-${i + 1}`,
        name: `Mock Campaign ${i + 1}`,
        state: ['enabled', 'paused'][i % 2],
        dailyBudget: parseFloat((Math.random() * 100 + 10).toFixed(2)),
        startDate,
        endDate: null,
        campaignType: 'sponsored_products',
      }));
      
      // Comprehensive dashboard metrics
      const dashboardMetrics = {
        // Performance metrics
        adSales: 114700,
        adSalesChange: 23.18,
        previousAdSales: 93100,
        adSpend: 2329,
        adSpendChange: 4.88,
        previousAdSpend: 2221,
        roas: 49.24,
        roasChange: 17.45,
        previousRoas: 41.93,
        totalSales: 305700,
        totalSalesChange: 36.01,
        previousTotalSales: 224800,
        
        // Efficiency metrics
        acos: 2.03,
        acosChange: -14.86,
        previousAcos: 2.39,
        cpc: 4.955,
        cpcChange: -1.36,
        previousCpc: 5.024,
        cvr: 10.64,
        cvrChange: 4.49,
        previousCvr: 10.18,
        tacos: 0.76,
        tacosChange: -22.88,
        previousTacos: 0.99,
        
        // Awareness metrics
        impressions: 49458,
        impressionsChange: -5.22,
        previousImpressions: 52182,
        clicks: 470,
        clicksChange: 6.33,
        previousClicks: 442,
        ctr: 0.95,
        ctrChange: 12.19,
        previousCtr: 0.85,
        totalOrders: 121,
        totalOrdersChange: 21,
        previousTotalOrders: 100,
        
        // Store overview
        avgOrderValue: 2526,
        avgOrderChange: 1.71,
        previousAvgOrderValue: 2484,
        totalProducts: 8,
        activeProducts: 6,
        inactiveProducts: 2,
        totalCampaigns: 11,
        activeCampaigns: campaigns.filter(c => c.state === 'enabled').length,
        
        // Budget report
        budgetReport: [
          { range: '90-100%', campaigns: 1 },
          { range: '80-90%', campaigns: 2 },
          { range: '20-80%', campaigns: 3 },
          { range: '0-20%', campaigns: 1 }
        ],
        
        // Growth readiness
        growthReadiness: {
          spend: 15,
          maxSpend: 100,
          revenue: 48.6,
          maxRevenue: 100
        },
        
        // Brand store usage (last 30 days)
        brandStoreUsage: {
          visits: 4568,
          visitsChange: 12.5,
          views: 18234,
          viewsChange: 8.3,
          sales: 125600,
          salesChange: 15.7,
          salesFromBrandStore: 18.2
        },
        
        // Order summary (last 7 days)
        orderSummary: [
          { day: 'Mon', orders: 15 },
          { day: 'Tue', orders: 18 },
          { day: 'Wed', orders: 22 },
          { day: 'Thu', orders: 17 },
          { day: 'Fri', orders: 20 },
          { day: 'Sat', orders: 16 },
          { day: 'Sun', orders: 13 }
        ]
      };
      
      return mockResponse({ 
        success: true, 
        data: { 
          metrics: dashboardMetrics,
          campaigns,
          dateRange: { startDate, endDate }
        } 
      });
    }
    return api.get(endpoints.amazon.dashboard, {
      params: { profileId, startDate, endDate }
    });
  },
  
  // Get campaigns for a profile (cached until mutation or TTL; pass { forceRefresh: true } to bypass)
  getCampaigns: (profileId, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    if (USE_MOCK_AMAZON) {
      const campaigns = Array.from({ length: 12 }, (_, i) => ({
        campaignId: `C-${i + 1}`,
        name: `Mock Campaign ${i + 1}`,
        state: ['enabled', 'paused', 'archived'][i % 3],
        dailyBudget: parseFloat((Math.random() * 100 + 10).toFixed(2)),
        startDate: new Date().toISOString().slice(0, 10),
        endDate: null,
        campaignType: ['sponsored_products', 'sponsored_brands', 'sponsored_display'][i % 3],
      }));
      return mockResponse({ success: true, data: campaigns });
    }
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.CAMPAIGNS,
      scope: 'all',
      forceRefresh,
      fetcher: () => api.get(endpoints.amazon.campaigns, { params: { profileId } }),
    });
  },
  
  // Get campaign metrics
  getCampaignMetrics: (profileId, campaignId, startDate, endDate) => 
    api.get(endpoints.amazon.campaignsMetrics, {
      params: { profileId, campaignId, startDate, endDate }
    }),
  
  // Get ad groups (cached per campaign until mutation or TTL)
  getAdGroups: (profileId, campaignId, campaignType, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    const scope = String(campaignId || 'all');
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.AD_GROUPS,
      scope,
      forceRefresh,
      fetcher: () => api.get(endpoints.amazon.adGroups, {
        params: { profileId, campaignId, campaignType }
      }),
    });
  },
  
  // Get AdGroup performance data from MongoDB (cached per date range)
  getAdGroupsPerformance: (profileId, campaignId, startDate, endDate, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    const scope = [campaignId || 'all', startDate, endDate].join(':');
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.AD_GROUP_METRICS,
      scope,
      forceRefresh,
      ttlMs: metricsTtlFor(endDate),
      fetcher: () => api.get('/performance/adgroups', {
        params: { profileId, campaignId, startDate, endDate }
      }),
    });
  },

  // Get Keyword performance data from MongoDB (cached per date range)
  getKeywordsPerformance: (profileId, campaignId, adGroupId, startDate, endDate, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    const scope = [campaignId || 'all', adGroupId || 'all', startDate, endDate].join(':');
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.KEYWORD_METRICS,
      scope,
      forceRefresh,
      ttlMs: metricsTtlFor(endDate),
      fetcher: () => api.get('/performance/keywords', {
        params: {
          profileId,
          campaignId,
          adGroupId,
          startDate,
          endDate
        }
      }),
    });
  },

  getProductAdsPerformance: (profileId, campaignId, adGroupId, startDate, endDate, asins, options = {}) => {
    const { groupBy, forceRefresh = false } = options;
    const scope = [campaignId || 'all', adGroupId || 'all', startDate, endDate, groupBy || 'asin'].join(':');
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.PRODUCT_METRICS,
      scope,
      forceRefresh,
      ttlMs: METRICS_TTL_MS,
      fetcher: () => api.get('/performance/productads', {
        params: {
          profileId,
          startDate,
          endDate,
          ...(campaignId ? { campaignId } : {}),
          ...(adGroupId ? { adGroupId } : {}),
          ...(asins ? { asins } : {}),
          ...(groupBy ? { groupBy } : {})
        }
      }),
    });
  },
  
  // Get keywords (cached per campaign/ad group; refresh=true bypasses server cache, forceRefresh bypasses client cache)
  getKeywords: (profileId, campaignId, adGroupId, campaignType, options = {}) => {
    const { refresh = false, forceRefresh = false } = options;
    const typeKey = normalizeCampaignType(campaignType) || 'unknown';
    const scope = [typeKey, campaignId || 'all', adGroupId || 'all'].join(':');
    const serverRefresh = refresh || forceRefresh;
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.KEYWORDS,
      scope,
      forceRefresh,
      fetcher: () => api.get(endpoints.amazon.keywords, {
        params: {
          profileId,
          ...(campaignId ? { campaignId } : {}),
          ...(adGroupId ? { adGroupId } : {}),
          ...(campaignType ? { campaignType } : {}),
          ...(serverRefresh ? { refresh: 'true' } : {})
        }
      }),
    });
  },
  
  // Negative Keywords (cached per campaign/ad group)
  getNegativeKeywords: (profileId, campaignId, adGroupId, campaignType, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    const scope = [
      normalizeCampaignType(campaignType) || 'unknown',
      campaignId || 'all',
      adGroupId || 'all',
    ].join(':');
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.NEGATIVE_KEYWORDS,
      scope,
      forceRefresh,
      fetcher: () => api.get(endpoints.amazon.negativeKeywords, {
        params: { profileId, campaignId, adGroupId, campaignType }
      }),
    });
  },
  
  getCampaignNegativeKeywords: (profileId, campaignId, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    const scope = String(campaignId || 'all');
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.CAMPAIGN_NEGATIVE_KEYWORDS,
      scope,
      forceRefresh,
      fetcher: () => api.get(endpoints.amazon.campaignNegativeKeywords, {
        params: { profileId, campaignId }
      }),
    });
  },
  
  createNegativeKeywords: (profileId, negativeKeywords) =>
    api.post(endpoints.amazon.negativeKeywords, {
      profileId,
      negativeKeywords
    }),
  
  createCampaignNegativeKeywords: (profileId, negativeKeywords) =>
    api.post(endpoints.amazon.campaignNegativeKeywords, {
      profileId,
      negativeKeywords
    }),
  
  deleteNegativeKeyword: (profileId, negativeKeywordId) =>
    api.delete(`${endpoints.amazon.negativeKeywords}/${negativeKeywordId}`, {
      params: { profileId }
    }),

  deleteCampaignNegativeKeyword: (profileId, campaignNegativeKeywordId) =>
    api.delete(`/amazon/campaignNegativeKeywords/${campaignNegativeKeywordId}`, {
      params: { profileId }
    }),

  getNegativeTargets: (profileId, campaignId, adGroupId) =>
    api.get('/amazon/negativeTargets', {
      params: { profileId, campaignId, adGroupId }
    }),

  deleteNegativeTarget: (profileId, targetId) =>
    api.delete(`/amazon/negativeTargets/${targetId}`, {
      params: { profileId }
    }),

  getCampaignBudgetRules: (profileId, campaignId) =>
    api.get(`/amazon/campaigns/${campaignId}/budgetRules`, {
      params: { profileId }
    }),

  disassociateBudgetRule: (profileId, campaignId, budgetRuleId) =>
    api.delete('/amazon/rules/associate', {
      data: { profileId, campaignId, budgetRuleId }
    }),

  getSpCampaignById: (profileId, campaignId) =>
    api.get(`/amazon/sp/campaigns/${campaignId}`, {
      params: { profileId, _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    }),

  listSpTargetableAudiences: (profileId, { q, locale } = {}) =>
    api.get('/amazon/sp/targetable-audiences', {
      params: { profileId, q, locale, _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    }),

  listSbTargetableAudiences: (profileId, { q, locale } = {}) =>
    api.get('/amazon/sb/targetable-audiences', {
      params: { profileId, q, locale, _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    }),

  listSdTargetableAudiences: (profileId, { q, locale } = {}) =>
    api.get('/amazon/sd/targetable-audiences', {
      params: { profileId, q, locale, _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    }),

  getSdCampaignById: (profileId, campaignId) =>
    api.get(`/amazon/sd/campaigns/${campaignId}`, {
      params: { profileId, _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    }),

  updateSpCampaignOffAmazonReach: (profileId, campaignId, offAmazonSetting) =>
    api.put(`/amazon/sp/campaigns/${campaignId}/off-amazon-reach`, {
      profileId,
      offAmazonSetting,
    }),

  getCampaignOptimizationRule: (profileId, campaignId) =>
    api.get(`/amazon/campaigns/${campaignId}/campaignOptimization`, {
      params: { profileId, _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    }),

  saveCampaignOptimizationRule: (profileId, campaignId, body) =>
    api.post(`/amazon/campaigns/${campaignId}/campaignOptimization`, {
      profileId,
      ...body,
    }),

  deleteCampaignOptimizationRule: (profileId, campaignId) =>
    api.delete(`/amazon/campaigns/${campaignId}/campaignOptimization`, {
      params: { profileId, _t: Date.now() },
    }),
  
  // Get product ads (cached per profile or campaign/ad group scope)
  getProductAds: (profileId, campaignId, adGroupId, campaignType, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    if (USE_MOCK_AMAZON) {
      const productNames = [
        "Wireless Bluetooth Headphones - Premium Sound Quality",
        "Smart Fitness Tracker with Heart Rate Monitor",
        "Organic Green Tea - 100 Tea Bags Pack",
        "Stainless Steel Water Bottle - 32oz Insulated",
        "LED Desk Lamp with USB Charging Port",
        "Yoga Mat - Non-Slip Premium Quality",
        "Bluetooth Speaker - Portable Wireless",
        "Protein Powder - Vanilla Flavor 2lbs",
        "Phone Case - Clear Protective Cover",
        "Essential Oil Diffuser - Ultrasonic Aromatherapy",
        "Resistance Bands Set - 5 Different Levels",
        "Coffee Maker - Programmable Drip Coffee",
        "Laptop Stand - Adjustable Aluminum",
        "Face Moisturizer - Anti-Aging Formula",
        "Wireless Mouse - Ergonomic Design",
        "Vitamin C Serum - Brightening Skincare",
        "Travel Backpack - Waterproof Hiking",
        "Ceramic Dinner Plates Set - 16 Pieces",
        "Bluetooth Earbuds - Noise Cancelling",
        "Hair Dryer - Ionic Technology",
        "Kitchen Knife Set - Professional Grade",
        "Essential Oils Kit - 6 Bottles Set",
        "Wireless Charging Pad - Fast Charging",
        "Bamboo Cutting Board - Eco-Friendly"
      ];
      
      const productAds = productNames.map((name, i) => {
        const price = parseFloat((Math.random() * 200 + 10).toFixed(2));
        const impressions = Math.floor(Math.random() * 10000) + 500;
        const clicks = Math.floor(impressions * (Math.random() * 0.05 + 0.01)); // 1-6% CTR
        const spend = parseFloat((clicks * (Math.random() * 3 + 0.5)).toFixed(2));
        const orders = Math.floor(clicks * (Math.random() * 0.15 + 0.02)); // 2-17% CVR
        const adSales = parseFloat((orders * price * (Math.random() * 1.5 + 0.8)).toFixed(2));
        const totalSales = parseFloat((adSales * (Math.random() * 1.3 + 1)).toFixed(2));
        const acos = adSales > 0 ? (spend / adSales * 100) : 0;
        const roas = spend > 0 ? (adSales / spend) : 0;
        const ctr = impressions > 0 ? (clicks / impressions * 100) : 0;
        const cvr = clicks > 0 ? (orders / clicks * 100) : 0;
        
        return {
          adId: `AD-${i + 1}`,
          asin: `B0${String(i + 1).padStart(8, '0')}`,
          sku: `SKU-${i + 1}`,
          title: name,
          productName: name,
          price,
          currency: 'INR',
          state: ['enabled', 'paused'][i % 2],
          image: `https://via.placeholder.com/150x150?text=Product+${i + 1}`,
          imageUrl: `https://via.placeholder.com/150x150?text=Product+${i + 1}`,
          category: ['Electronics', 'Health & Beauty', 'Home & Kitchen', 'Sports'][i % 4],
          brand: ['TechBrand', 'HealthPlus', 'HomeStyle', 'SportMax'][i % 4],
          // Performance metrics
          impressions,
          clicks,
          spend,
          orders,
          adSales,
          totalSales,
          sales: adSales, // Alias
          acos,
          roas,
          ctr,
          cvr
        };
      });
      return mockResponse({ success: true, data: productAds });
    }
    const typeKey = normalizeCampaignType(campaignType) || 'unknown';
    const scope = [typeKey, campaignId || 'all', adGroupId || 'all'].join(':');
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.PRODUCT_ADS,
      scope,
      forceRefresh,
      fetcher: () => api.get(endpoints.amazon.productAds, {
        params: { profileId, campaignId, adGroupId, campaignType }
      }),
    });
  },
  
  // Get search terms from MongoDB
  getSearchTerms: (profileId, campaignId, adGroupId, startDate, endDate, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    const scope = searchTermsCacheScope(campaignId, adGroupId, startDate, endDate);
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.SEARCH_TERMS,
      scope,
      forceRefresh,
      ttlMs: METRICS_TTL_MS,
      fetcher: () => api.get('/search-terms', {
        params: { profileId, campaignId, adGroupId, startDate, endDate }
      }),
    });
  },
  
  // Get product/ASIN targeting metrics from MongoDB
  getTargetingData: (profileId, campaignId, adGroupId, startDate, endDate, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    const scope = [campaignId || 'all', adGroupId || 'all', startDate, endDate].join(':');
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.TARGETING_METRICS,
      scope,
      forceRefresh,
      ttlMs: metricsTtlFor(endDate),
      fetcher: () => api.get('/targeting', {
        params: { profileId, campaignId, adGroupId, startDate, endDate }
      }),
    });
  },

  /** Live Amazon targets + unified/stream metrics (Campaign Manager product targeting). */
  getCampaignManagerTargeting: (
    profileId,
    campaignId,
    adGroupId,
    startDate,
    endDate,
    campaignType,
    options = {}
  ) => {
    const forceRefresh = options?.forceRefresh === true;
    const typeKey = normalizeCampaignType(campaignType) || 'unknown';
    const scope = [typeKey, campaignId, adGroupId, startDate, endDate].join(':');
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.CAMPAIGN_MANAGER_TARGETING,
      scope,
      forceRefresh,
      ttlMs: metricsTtlFor(endDate),
      fetcher: () => api.get('/targeting/cm', {
        params: {
          profileId,
          campaignId,
          adGroupId,
          startDate,
          endDate,
          campaignType,
        },
      }),
    });
  },
  
  // Get targets from Amazon Live API (includes auto + product targets)
  getTargets: (profileId, campaignId, adGroupId, campaignType, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    const typeKey = normalizeCampaignType(campaignType) || 'unknown';
    const scope = [typeKey, campaignId || 'all', adGroupId || 'all'].join(':');
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.TARGETS,
      scope,
      forceRefresh,
      fetcher: () => api.get(endpoints.amazon.targets, {
        params: { profileId, campaignId, adGroupId, campaignType }
      }),
    });
  },

  getCampaignObjects: (profileId, campaignId, campaignType) =>
    api.get(`${endpoints.amazon.campaigns}/${campaignId}/objects`, {
      params: { profileId, campaignType }
    }),
  
  // Get SD targets from Amazon Live API
  getSdTargets: (profileId, campaignId) =>
    api.post('/amazon/targets/sd/list', {
      profileId,
      campaignIdFilter: {
        include: [Number(campaignId)]
      }
    }),
  
  // Get SD creatives from Amazon Live API
  getSdCreatives: (profileId, adGroupId) =>
    api.get('/amazon/creatives/sd', {
      params: { profileId, adGroupIdFilter: adGroupId }
    }),

  getSbCampaignSettings: (profileId, campaignId) =>
    api.get(`/amazon/sb/campaigns/${campaignId}/settings`, {
      params: { profileId },
    }),

  getSbAdGroupSettings: (profileId, adGroupId, campaignId) =>
    api.get('/amazon/adGroups/sb/settings', {
      params: { profileId, adGroupId, campaignId },
    }),

  getSbThemes: (profileId, campaignId, adGroupId, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    const scope = [campaignId || 'all', adGroupId || 'all'].join(':');
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.SB_THEMES,
      scope,
      forceRefresh,
      fetcher: () => api.get('/amazon/sb/themes', {
        params: { profileId, campaignId, adGroupId },
      }),
    });
  },

  createSbKeywords: (profileId, keywords) =>
    api.post('/amazon/sb/keywords', { profileId, keywords }),

  updateSbKeywords: (profileId, keywords) =>
    api.put('/amazon/sb/keywords', { profileId, keywords }),

  /**
   * ONE request for the whole selection (server batches 100/call to Amazon).
   * items: [{ id, entityType: 'sp_keyword'|'sp_target'|'sd_target'|'sb_keyword'|'sb_theme', bid?, state?, campaignId?, adGroupId? }]
   * -> { results: [{ id, entityType, ok, error? }], summary }
   */
  bulkUpdateEntities: (profileId, items, entityType) =>
    api.post('/amazon/entities/bulk-update', { profileId, entityType, items }, { timeout: 180000 }),

  bulkChangeKeywordMatchType: (profileId, matchType, keywords) =>
    api.post('/amazon/entities/bulk-match-type', { profileId, matchType, keywords }, { timeout: 180000 }),

  createSbNegativeKeywords: (profileId, negativeKeywords) =>
    api.post('/amazon/sb/negativeKeywords', { profileId, negativeKeywords }),

  listSbBrands: (profileId, params = {}) =>
    api.get('/amazon/sb/brands', { params: { profileId, ...params } }),

  createSbCampaign: (profileId, campaign) =>
    api.post('/amazon/sb/campaigns', { profileId, campaign }),

  createSbAdGroups: (profileId, adGroups) =>
    api.post('/amazon/sb/adGroups', { profileId, adGroups }),

  updateSbAdGroups: (profileId, adGroups) =>
    api.put('/amazon/sb/adGroups', { profileId, adGroups }),

  createSbAd: (profileId, { adGroupId, name, campaignData, products }) =>
    api.post('/amazon/sb/ads', {
      profileId,
      adGroupId,
      name,
      campaignData,
      products,
    }),

  finishSbAdGroupLaunch: (profileId, payload) =>
    api.post('/amazon/sb/adGroups/finishLaunch', { profileId, ...payload }),

  updateSbAd: (profileId, adId, { state, name } = {}) =>
    api.put(`/amazon/sb/ads/${adId}`, { profileId, state, name }),

  getSbAdViewProducts: (profileId, adId, { campaignId, adGroupId, debug } = {}) =>
    api.get(`/amazon/sb/ads/${adId}/view-products`, {
      params: { campaignId, adGroupId, profileId, ...(debug ? { debug: '1' } : {}) },
    }),

  getSbAdInspect: (profileId, adId, { campaignId, adGroupId } = {}) =>
    api.get(`/amazon/sb/ads/${adId}/inspect`, {
      params: { profileId, campaignId, adGroupId },
    }),

  updateSbAdCreative: (profileId, adId, { adFormat, creative } = {}) =>
    api.put(`/amazon/sb/ads/${adId}/creative`, { profileId, adFormat, creative }),

  createSdTargets: (profileId, targets) =>
    api.post(endpoints.amazon.targets, {
      profileId,
      campaignType: 'sponsored_display',
      targets,
    }),

  updateSdCreative: (profileId, creativeId, properties, options = {}) =>
    api.put(`/amazon/creatives/sd/${creativeId}`, {
      profileId,
      properties,
      adGroupId: options.adGroupId,
      campaignId: options.campaignId,
      saveScope: options.saveScope,
    }),

  createSdCreative: (profileId, payload) =>
    api.post('/amazon/creatives/sd', { profileId, ...payload }),

  getSdCreativeLibrary: (profileId, adGroupId) =>
    api.get('/amazon/creatives/sd/library', {
      params: { profileId, adGroupId },
    }),

  saveSdCreativeLibrary: (profileId, payload) =>
    api.post('/amazon/creatives/sd/library', { profileId, ...payload }),

  getSdNegativeTargets: (profileId, campaignId, adGroupId) =>
    api.get('/amazon/sd/negativeTargets', {
      params: { profileId, campaignId, adGroupId },
    }),

  createSdNegativeTargets: (profileId, targets) =>
    api.post('/amazon/sd/negativeTargets', { profileId, targets }),

  deleteSdNegativeTarget: (profileId, targetId) =>
    api.delete(`/amazon/sd/negativeTargets/${targetId}`, {
      params: { profileId },
    }),

  createSbThemes: (profileId, themes) =>
    api.post('/amazon/sb/themes', { profileId, themes }),

  updateSbThemes: (profileId, themes) =>
    api.put('/amazon/sb/themes', { profileId, themes }),

  createSbTargets: (profileId, targets) =>
    api.post('/amazon/sb/targets', { profileId, targets }),

  searchCreativeAssets: (profileId, { assetType, assetSubType, text, pageSize } = {}) =>
    api.post('/amazon/assets/search', {
      profileId,
      assetType,
      assetSubType,
      text,
      pageSize,
    }),

  uploadBrandLogo: (profileId, file, brandEntityId) => {
    const form = new FormData();
    form.append('profileId', String(profileId));
    if (brandEntityId) form.append('brandEntityId', String(brandEntityId));
    form.append('file', file);
    return api.post('/amazon/assets/brand-logo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },

  uploadCreativeAsset: (profileId, file, { assetType = 'IMAGE', assetSubType = 'LOGO', name } = {}) => {
    const form = new FormData();
    form.append('profileId', String(profileId));
    form.append('assetType', assetType);
    form.append('assetSubType', assetSubType);
    if (name) form.append('name', name);
    form.append('file', file);
    return api.post('/amazon/assets/creative', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },

  createSdProductAds: (profileId, productAds) =>
    api.post('/amazon/adGroups/sd/productAds', { profileId, productAds }),
  
  // Create campaign
  createCampaign: (profileId, campaignData) => {
    if (USE_MOCK_AMAZON) {
      return mockResponse([{ campaignId: `C-${Math.floor(Math.random() * 10000)}`, ...campaignData }]);
    }
    return api.post(endpoints.amazon.createCampaign, {
      profileId,
      ...campaignData
    });
  },

  // Create ad groups
  createAdGroups: (profileId, adGroupData) => {
    const payload = {
      profileId: String(profileId),
      campaignType: adGroupData.campaignType || undefined,
      adGroups: (adGroupData.adGroups || [adGroupData]).map(adGroup => ({
        ...adGroup,
        campaignId: Number(adGroup.campaignId),
        defaultBid: Number(adGroup.defaultBid)
      }))
    };
    console.log('📤 api.js createAdGroups - sending payload:', JSON.stringify(payload, null, 2));
    return api.post(endpoints.amazon.adGroups, payload);
  },

  // Create product ads
  createProductAds: (profileId, productAdsData) => {
    return api.post(endpoints.amazon.productAds, {
      profileId,
      ...productAdsData
    });
  },

  resolveLauncherProductSkus: (profileId, products) =>
    api.post('/amazon/products/resolve-skus', { profileId, products }),

  searchAdvertisableProducts: (profileId, params) =>
    api.post('/amazon/products/metadata-search', { profileId, ...params }),

  updateProductAds: (profileId, productAds, campaignType) =>
    api.put(endpoints.amazon.productAds, {
      profileId,
      productAds,
      ...(campaignType ? { campaignType } : {}),
    }),

  // Create keywords
  createKeywords: (profileId, keywordsData) => {
    return api.post(endpoints.amazon.keywords, {
      profileId,
      keywords: keywordsData.keywords || [keywordsData]
    });
  },

  // Create targeting (product/category)
  createTargeting: (profileId, targetingData) => {
    return api.post('/amazon/targets', {
      profileId,
      ...targetingData
    });
  },

  createNegativeTargets: (profileId, negativeTargetingClauses) => {
    return api.post('/amazon/negativeTargets', {
      profileId,
      negativeTargetingClauses
    });
  },

  getCampaignNegativeTargets: (profileId, campaignId) =>
    api.get('/amazon/campaignNegativeTargets', {
      params: { profileId, campaignId },
    }),

  createCampaignNegativeTargets: (profileId, campaignNegativeTargetingClauses) =>
    api.post('/amazon/campaignNegativeTargets', {
      profileId,
      campaignNegativeTargetingClauses,
    }),

  deleteCampaignNegativeTarget: (profileId, targetId) =>
    api.delete(`/amazon/campaignNegativeTargets/${targetId}`, {
      params: { profileId },
    }),

  // Get product recommendations
  getProductRecommendations: (profileId, data) => {
    return api.post('/amazon/product-recommendations', {
      profileId,
      ...data
    });
  },

  // Get category recommendations
  getCategoryRecommendations: (profileId, data) => {
    return api.post('/amazon/category-recommendations', {
      profileId,
      ...data
    });
  },

  getKeywordRecommendations: (profileId, data) =>
    api.post('/amazon/keyword-recommendations', {
      profileId,
      ...data,
    }),

  searchTargetableCategories: (profileId, params = {}) =>
    api.get('/amazon/targetable-categories', {
      params: { profileId, ...params },
    }),

  getCategoryNames: (profileId, ids) =>
    api.get('/amazon/category-names', {
      params: { profileId, ids: Array.isArray(ids) ? ids.join(',') : ids },
    }),
  
  // Get targeting suggestions
  getTargetingSuggestions: (profileId, asin, maxSuggestions = 100) => 
    api.get(endpoints.amazon.targetingSuggestions, {
      params: { profileId, asin, maxSuggestions }
    }),
  
  // Get budgets
  getBudgets: (profileId) => 
    api.get(endpoints.amazon.budgets, {
      params: { profileId }
    }),
  
  // Dashboard summary (metrics + chart series for selected period)
  getDashboardSummary: (profileId, params = {}, options = {}) => {
    const { period, startDate, endDate, metricsMode } = params;
    const mode = metricsMode || 'merged';
    const forceRefresh = options?.forceRefresh === true;
    const scope = [period || 'custom', startDate || '', endDate || '', mode].join(':');
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.DASHBOARD,
      scope,
      forceRefresh,
      ttlMs: mode === 'unified' ? 2 * 60 * 1000 : metricsTtlFor(endDate),
      fetcher: () => api.get(endpoints.amazon.dashboard, {
        params: { profileId, period, startDate, endDate, metricsMode: mode },
        timeout: mode === 'unified' ? 15000 : 120000,
      }),
    });
  },

  getDashboardDataSources: (profileId, startDate, endDate) =>
    api.get('/amazon/dashboard/data-sources', {
      params: { profileId, startDate, endDate },
    }),

  getDashboardCompleteness: (profileId, startDate, endDate) =>
    api.get('/amazon/dashboard/completeness', {
      params: { profileId, startDate, endDate },
    }),

  // Reporting API - Get cached report data (FAST - no API call to Amazon)
  getBulkSnapshot: (profileId, startDate, endDate, level) =>
    api.get(endpoints.amazon.bulkSnapshot, {
      params: { profileId, startDate, endDate, level }
    }),

  getBulkSnapshotBundle: (profileId, startDate, endDate, levels) =>
    api.get(endpoints.amazon.bulkSnapshotBundle, {
      params: {
        profileId,
        startDate,
        endDate,
        levels: Array.isArray(levels) ? levels.join(',') : levels,
      },
    }),

  postOptimizationWarmup: (profileId, startDate, endDate, { forceRefresh = false } = {}) =>
    api.post('/amazon/optimization-warmup', {
      profileId,
      startDate,
      endDate,
      forceRefresh,
    }, { timeout: 120000 }),

  getCachedReport: (profileId, startDate, endDate, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    const scope = `${startDate}:${endDate}`;
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.CACHED_REPORT,
      scope,
      forceRefresh,
      ttlMs: metricsTtlFor(endDate),
      fetcher: () => api.get(endpoints.amazon.reportingCached, {
        params: { profileId, startDate, endDate },
      }),
    });
  },

  // Get cache metadata (last refresh time, status, etc.)
  getCacheMetadata: (profileId) =>
    api.get(endpoints.amazon.reportingMetadata, {
      params: { profileId }
    }),

  // Get search term report data (customer search queries)
  getSearchTermReport: (profileId, startDate, endDate) =>
    api.get('/amazon/reports/searchTerms', {
      params: { profileId, startDate, endDate }
    }),

  // Get MY store ASINs with images from MongoDB
  getMyStoreASINs: (profileId) =>
    api.get('/my-store/my-store-asins', {
      params: { profileId }
    }),

  // Get MY store ASINs with advertising metrics
  getMyStoreASINsWithMetrics: (profileId, startDate, endDate, options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    const scope = `${startDate}:${endDate}`;
    return fetchLiveCached({
      profileId,
      entity: LIVE_ENTITIES.STORE_PRODUCTS,
      scope,
      forceRefresh,
      ttlMs: METRICS_TTL_MS,
      fetcher: () => api.get('/my-store/my-store-asins-with-metrics', {
        params: { profileId, startDate, endDate },
      }),
    });
  },

  getAsinImageStats: (profileId) =>
    api.get('/asin-images/stats', { params: { profileId } }),

  getAsinImageStatus: (profileId) =>
    api.get('/asin-images/status', { params: { profileId } }),

  syncAsinImages: (profileId, retryFailed = false) =>
    api.post('/asin-images/sync', { profileId, retryFailed }),

  syncAsinCatalogForAsins: (profileId, asins, { forceResync = false } = {}) =>
    api.post('/asin-images/sync', { profileId, asins, forceResync }),

  getAsinCatalogBatch: (asins) =>
    api.get('/asin-images/catalog', {
      params: { asins: (Array.isArray(asins) ? asins : []).join(',') },
    }),

  resyncAsinCatalog: (profileId) =>
    api.post('/asin-images/sync', { profileId, forceResync: true }),

  cancelAsinImageSync: (profileId) =>
    api.post('/asin-images/cancel', { profileId }),

  // Reporting API - Create ad spend report
  createReport: (profileId, startDate, endDate, configuration) => 
    api.post(endpoints.amazon.reportingCreate, {
      profileId,
      startDate,
      endDate,
      configuration,
      name: 'Ad Spend Report'
    }),
  
  // Reporting API - Check report status
  getReportStatus: (reportId, profileId) => 
    api.get(endpoints.amazon.reportingStatus(reportId), {
      params: { profileId }
    }),
  
  // Reporting API - Download report data (includes cost/spend)
  downloadReport: (reportId, profileId, startDate, endDate) => 
    api.get(endpoints.amazon.reportingDownload(reportId), {
      params: { profileId, startDate, endDate }
    }),
  
  // Helper function to get ad spend data (handles full flow)
  getAdSpendData: async (profileId, startDate, endDate) => {
    try {
      // Step 1: Create report
      const createResponse = await api.post(endpoints.amazon.reportingCreate, {
        profileId,
        startDate,
        endDate,
        name: 'Ad Spend Report'
      });
      
      if (!createResponse.data.success || !createResponse.data.reportId) {
        throw new Error('Failed to create report');
      }
      
      const reportId = createResponse.data.reportId;
      
      // Step 2: Poll for completion (check every 30 seconds, up to 20 minutes)
      const maxAttempts = 40; // 40 * 30s = 20 minutes
      let attempt = 0;
      
      while (attempt < maxAttempts) {
        // Wait before checking (except first time)
        if (attempt > 0) {
          await new Promise(resolve => setTimeout(resolve, 30000)); // 30 seconds
        }
        
        const statusResponse = await api.get(endpoints.amazon.reportingStatus(reportId), {
          params: { profileId }
        });
        
        const status = statusResponse.data.status;
        
        if (status === 'COMPLETED') {
          // Step 3: Download report
          const downloadResponse = await api.get(endpoints.amazon.reportingDownload(reportId), {
            params: { profileId }
          });
          
          return downloadResponse.data;
        } else if (status === 'FAILED') {
          throw new Error('Report generation failed');
        }
        
        attempt++;
      }
      
      throw new Error('Report generation timeout');
    } catch (error) {
      console.error('Error getting ad spend data:', error);
      throw error;
    }
  },

  // ==========================================
  // AUTO TARGETING METHODS (for Auto Campaigns)
  // ==========================================
  
  /**
   * Get auto targets for an ad group (auto campaigns only)
   * Returns 4 auto-created targeting expressions:
   * - QUERY_HIGH_REL_MATCHES (close match)
   * - QUERY_BROAD_REL_MATCHES (loose match)
   * - ASIN_ACCESSORY_RELATED (complements)
   * - ASIN_SUBSTITUTE_RELATED (substitutes)
   */
  getAutoTargets: (profileId, campaignId, adGroupId, campaignType, options = {}) =>
    amazonApi.getTargets(profileId, campaignId, adGroupId, campaignType, options),

  updateAutoTarget: (profileId, targetId, updates) =>
    api.put(`${endpoints.amazon.targets}/${targetId}`, {
      profileId,
      updates
    }),

  updateAutoTargetBid: (profileId, targetId, bid) =>
    api.put(`${endpoints.amazon.targets}/${targetId}`, {
      profileId,
      updates: { bid: parseFloat(bid) }
    }),

  updateProductTargetBid: (profileId, targetId, bid) =>
    api.put(`${endpoints.amazon.targets}/${targetId}`, {
      profileId,
      updates: { bid: parseFloat(bid) }
    }),

  updateProductTarget: (profileId, targetId, updates) =>
    api.put(`${endpoints.amazon.targets}/${targetId}`, {
      profileId,
      updates,
    }),

  resolveProductTarget: (profileId, payload) =>
    api.post('/amazon/targets/resolve', {
      profileId,
      ...payload,
    }),

  resolveProductTargetsBatch: (profileId, items) =>
    api.post('/amazon/targets/resolve-batch', {
      profileId,
      items,
    }),

  updateSdTarget: (profileId, targetId, updates) =>
    api.put(`${endpoints.amazon.targets}/${targetId}`, {
      profileId,
      campaignType: 'sponsored_display',
      updates
    }),

  updateSdTargetBid: (profileId, targetId, bid) =>
    api.put(`${endpoints.amazon.targets}/${targetId}`, {
      profileId,
      campaignType: 'sponsored_display',
      updates: { bid: parseFloat(bid) }
    }),
  
  /**
   * Get bid recommendations for auto targets
   */
  getAutoTargetBidRecommendations: (profileId, campaignId, adGroupId) => 
    api.post(endpoints.amazon.targetBidRecommendations, {
      profileId,
      campaignId,
      adGroupId
    }),

  getTargetBidRecommendations: (profileId, targetingClauses) =>
    api.post(endpoints.amazon.targetBidRecommendations, {
      profileId,
      targetingClauses
    }),
};

// SP-API (Selling Partner API) methods
export const spApi = {
  // Get product catalog item by ASIN
  getCatalogItem: (asin, marketplaceIds = 'A21TJRUUN4KGV') =>
    api.get(`/sp-api/catalog/items/${asin}`, {
      params: { marketplaceIds }
    }),
  
  // Search catalog items
  searchCatalogItems: (params) =>
    api.get('/sp-api/catalog/items', { params }),
  
  // Get product pricing
  getProductPricing: (asin, marketplaceIds = 'A21TJRUUN4KGV') =>
    api.get(`/sp-api/product-pricing/items/${asin}`, {
      params: { marketplaceIds }
    }),
  
  // Get sales and traffic report
  getSalesTrafficReport: (startDate, endDate, marketplaceIds = 'A21TJRUUN4KGV') =>
    api.get('/sp-api/reports/sales-traffic', {
      params: { startDate, endDate, marketplaceIds }
    }),
  
  // Get report status/document
  getReport: (reportId) =>
    api.get(`/sp-api/reports/${reportId}`)
};

export {
  invalidateLiveEntityCache,
  clearAllLiveEntityCache,
} from '../utils/liveEntityCache';

export default api;

